import React from 'react';
import { gql } from '@soundxyz/gql-string';
import type { ShareModalSource } from '@soundxyz/utils/analytics';
import { Input } from '@components/basics/forms/inputs/Input';
import { Button } from '@components/main-components/button/Button';
import { StandardModal } from '@components/main-components/modals/StandardModal';

import {
  REGULAR_POST_NO_URL_SHARE_TEXT,
  REGULAR_POST_SHARE_TEXT,
  TwitterPostShareLink,
} from '@components/modals/ShareModalHelpers';
import { useModal } from '@context/modal';
import { type FragmentType, getFragment, SharePostInfoFragmentDoc } from '@graphql/reactQuery';
import { useCopy } from '@hooks/useCopy';
import { trackClickEvent } from '@lib/analytics';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment SharePostInfo on Post {
    id
    webappUri
  }
`);

export function SharePostModal({
  post,
  sourceLocation,
}: {
  post: FragmentType<SharePostInfoFragmentDoc>;

  sourceLocation: ShareModalSource;
}) {
  const { closeModal } = useModal();

  const { id: postId, webappUri } = getFragment(SharePostInfoFragmentDoc, post);

  const { copy, isCopied } = useCopy({
    text: webappUri,
    successMessage: 'Link copied to clipboard',
  });

  const regularPostShareText = encodeURIComponent(REGULAR_POST_SHARE_TEXT(webappUri));

  return (
    <StandardModal
      closeModal={() => {
        closeModal();
        trackClickEvent({
          event: 'Share Modal Closed',
          properties: {
            associationType: 'Post',
            associationId: postId,
            sourceComponent: sourceLocation,
          },
        });
      }}
      body={
        <div className="flex flex-col items-center gap-3">
          <div className="flex flex-row gap-3">
            <a
              href={TwitterPostShareLink({ webappUri })}
              target="_blank"
              className="transition-all ease-in-out hover:brightness-[116%]"
              onClick={() => {
                trackClickEvent({
                  event: 'Share Modal Third Party Share',
                  properties: {
                    associationType: 'Post',
                    associationId: postId,
                    sourceComponent: sourceLocation,
                    thirdParty: 'Twitter',
                  },
                });
              }}
            >
              <button type="button" aria-label="Share to X (Twitter)" title="Share to X (Twitter)">
                <svg
                  width="47"
                  height="47"
                  viewBox="0 0 47 47"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect x="0.5" y="0.5" width="46" height="46" rx="23" fill="#26A7DE" />
                  <path
                    d="M31.4297 19.9375C31.4297 20.1328 31.4297 20.2891 31.4297 20.4844C31.4297 25.9141 27.3281 32.125 19.7891 32.125C17.4453 32.125 15.2969 31.4609 13.5 30.2891C13.8125 30.3281 14.125 30.3672 14.4766 30.3672C16.3906 30.3672 18.1484 29.7031 19.5547 28.6094C17.7578 28.5703 16.2344 27.3984 15.7266 25.7578C16 25.7969 16.2344 25.8359 16.5078 25.8359C16.8594 25.8359 17.25 25.7578 17.5625 25.6797C15.6875 25.2891 14.2812 23.6484 14.2812 21.6562V21.6172C14.8281 21.9297 15.4922 22.0859 16.1562 22.125C15.0234 21.3828 14.3203 20.1328 14.3203 18.7266C14.3203 17.9453 14.5156 17.2422 14.8672 16.6562C16.8984 19.1172 19.9453 20.7578 23.3438 20.9531C23.2656 20.6406 23.2266 20.3281 23.2266 20.0156C23.2266 17.75 25.0625 15.9141 27.3281 15.9141C28.5 15.9141 29.5547 16.3828 30.3359 17.2031C31.2344 17.0078 32.1328 16.6562 32.9141 16.1875C32.6016 17.1641 31.9766 17.9453 31.1172 18.4531C31.9375 18.375 32.7578 18.1406 33.4609 17.8281C32.9141 18.6484 32.2109 19.3516 31.4297 19.9375Z"
                    fill="white"
                  />
                </svg>
              </button>
            </a>

            <a
              href={`https://warpcast.com/~/compose?text=${regularPostShareText}`}
              target="_blank"
              className="transition-all ease-in-out hover:brightness-[116%]"
              onClick={() => {
                trackClickEvent({
                  event: 'Share Modal Third Party Share',
                  properties: {
                    associationType: 'Post',
                    associationId: postId,
                    sourceComponent: sourceLocation,
                    thirdParty: 'Warpcast',
                  },
                });
              }}
            >
              <button type="button" aria-label="Share to Warpcast" title="Share to Warpcast">
                <svg
                  width="47"
                  height="47"
                  viewBox="0 0 47 47"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect x="0.5" y="0.5" width="46" height="46" rx="23" fill="#472A91" />
                  <path
                    d="M28.4961 16.951L26.9187 22.875L25.3362 16.951H21.6944L20.0968 22.9186L18.5043 16.951H14.3564L18.2108 30.0489H21.7893L23.5004 23.9667L25.2115 30.0489H28.7977L32.6436 16.951H28.4961Z"
                    fill="white"
                  />
                </svg>
              </button>
            </a>

            <a
              href={`https://lenster.xyz/?text=${regularPostShareText}`}
              target="_blank"
              className="transition-all ease-in-out hover:brightness-[116%]"
              onClick={() => {
                trackClickEvent({
                  event: 'Share Modal Third Party Share',
                  properties: {
                    associationType: 'Post',
                    associationId: postId,
                    sourceComponent: sourceLocation,
                    thirdParty: 'Hey.xyz',
                  },
                });
              }}
            >
              <button type="button" aria-label="Share to Hey" title="Share to Hey">
                <svg
                  width="47"
                  height="47"
                  viewBox="0 0 47 47"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect x="0.5" y="0.5" width="46" height="46" rx="23" fill="#E74C61" />
                  <path
                    d="M28.9407 19.7522C28.8234 19.8714 28.6301 19.7898 28.6301 19.6225C28.6301 19.4697 28.6301 19.3013 28.6229 19.1423C28.442 12.5388 18.5576 12.5388 18.3768 19.1423C18.3726 19.3013 18.3706 19.4614 18.3706 19.6225C18.3706 19.784 18.1743 19.8663 18.06 19.7522C17.9477 19.6402 17.8314 19.5262 17.7171 19.4174C12.9473 14.8767 5.96253 21.9195 10.4715 26.7114C10.581 26.8271 10.6921 26.9422 10.805 27.0565C16.2455 32.534 23.4986 32.5345 23.4998 32.5345C23.5011 32.5345 30.7552 32.534 36.1957 27.0565C36.3093 26.9429 36.4204 26.8278 36.5291 26.7114C41.0382 21.9143 34.0492 14.8767 29.2836 19.4174C29.1683 19.5262 29.0508 19.6382 28.9407 19.7522Z"
                    fill="white"
                  />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M27.702 24.7153C27.5489 24.7153 27.496 24.9379 27.5936 25.0558C27.7664 25.2646 27.8704 25.5331 27.8704 25.8262C27.8704 26.491 27.335 27.0301 26.6746 27.0301C26.0141 27.0301 25.4787 26.491 25.4787 25.8262C25.4787 25.7906 25.4327 25.7729 25.4118 25.8017C25.2233 26.0625 25.096 26.3553 25.0437 26.6627C25.0142 26.8358 24.8745 26.9791 24.6989 26.9791H24.6019C24.3727 26.9791 24.1835 26.792 24.2174 26.5653C24.4493 25.0119 25.97 23.8853 27.702 23.8853C29.4341 23.8853 30.9548 25.0119 31.1867 26.5653C31.2205 26.792 31.0315 26.9791 30.8022 26.9791C30.5731 26.9791 30.3916 26.7911 30.3418 26.5674C30.115 25.5491 29.064 24.7153 27.702 24.7153ZM17.083 25.8262C17.083 25.7793 17.0226 25.7541 16.9942 25.7914C16.7926 26.0562 16.6553 26.3562 16.5971 26.6723C16.5629 26.859 16.412 27.0142 16.2222 27.0142H16.1509C15.9217 27.0142 15.7326 26.827 15.7664 26.6004C15.9982 25.0461 17.519 23.9203 19.2511 23.9203C20.9831 23.9203 22.504 25.0461 22.7358 26.6004C22.7695 26.827 22.5804 27.0142 22.3513 27.0142C22.1221 27.0142 21.9406 26.8262 21.8908 26.6024C21.6642 25.5835 20.6133 24.7503 19.2511 24.7503C19.1272 24.7503 19.0818 24.9255 19.1648 25.0175C19.3573 25.2312 19.4747 25.5149 19.4747 25.8262C19.4747 26.491 18.9393 27.0301 18.2788 27.0301C17.6184 27.0301 17.083 26.491 17.083 25.8262ZM25.4436 28.5985C25.2424 28.4886 24.992 28.5674 24.8301 28.7295C24.5178 29.0421 24.0444 29.2557 23.4966 29.2557C22.9473 29.2557 22.4744 29.0442 22.1629 28.7315C22.0011 28.5691 21.7511 28.4893 21.5496 28.5985C21.3481 28.7077 21.2709 28.9626 21.4153 29.1406C21.888 29.723 22.654 30.0857 23.4966 30.0857C24.3396 30.0857 25.104 29.7193 25.576 29.141C25.721 28.9635 25.6447 28.7084 25.4436 28.5985Z"
                    fill="#E74C61"
                  />
                </svg>
              </button>
            </a>

            <a
              href={`https://t.me/share/url?url=${webappUri}&text=${REGULAR_POST_NO_URL_SHARE_TEXT}`}
              target="_blank"
              className="transition-all ease-in-out hover:brightness-[116%]"
              onClick={() => {
                trackClickEvent({
                  event: 'Share Modal Third Party Share',
                  properties: {
                    associationType: 'Post',
                    associationId: postId,
                    sourceComponent: sourceLocation,
                    thirdParty: 'Telegram',
                  },
                });
              }}
            >
              <button type="button" aria-label="Share to Telegram" title="Share to Telegram">
                <svg
                  width="46"
                  height="46"
                  viewBox="0 0 46 46"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M23 0.96875C35.2188 0.96875 45.2812 11.0312 45.2812 23.25C45.2812 35.5586 35.2188 45.5312 23 45.5312C10.6914 45.5312 0.71875 35.5586 0.71875 23.25C0.71875 11.0312 10.6914 0.96875 23 0.96875ZM33.2422 16.1523C33.332 15.8828 33.332 15.6133 33.2422 15.2539C33.2422 15.0742 33.0625 14.8047 32.9727 14.7148C32.7031 14.4453 32.2539 14.4453 32.0742 14.4453C31.2656 14.4453 29.918 14.8945 23.7188 17.5C21.5625 18.3984 17.25 20.1953 10.7812 23.0703C9.70312 23.5195 9.16406 23.8789 9.07422 24.3281C8.98438 25.0469 10.1523 25.3164 11.5 25.7656C12.668 26.125 14.1953 26.5742 15.0039 26.5742C15.7227 26.5742 16.5312 26.3047 17.4297 25.6758C23.4492 21.543 26.5938 19.4766 26.7734 19.4766C26.9531 19.4766 27.1328 19.3867 27.2227 19.4766C27.4023 19.6562 27.4023 19.8359 27.3125 19.9258C27.2227 20.375 21.5625 25.5859 21.2031 25.9453C19.9453 27.2031 18.5078 28.0117 20.7539 29.4492C22.6406 30.707 23.7188 31.5156 25.6953 32.7734C26.9531 33.582 27.9414 34.5703 29.1992 34.4805C29.8281 34.3906 30.457 33.8516 30.7266 32.1445C31.5352 28.2812 32.9727 19.6562 33.2422 16.1523Z"
                    fill="#0088CC"
                  />
                </svg>
              </button>
            </a>
          </div>

          <div className="flex w-full flex-row">
            <Input
              disabled
              value={webappUri.replace('https://', '').replace('www.', '')}
              className="!text-black"
            />
            <Button
              variant="primary"
              label={isCopied ? 'Copied' : 'Copy'}
              disabled={isCopied}
              onClick={() => {
                copy();

                trackClickEvent({
                  event: 'Share Modal Third Copy Clipboard',
                  properties: {
                    associationType: 'Post',
                    associationId: postId,
                    sourceComponent: sourceLocation,
                  },
                });
              }}
            />
          </div>
        </div>
      }
      bodyWithoutPaddingBottom
      title="Share this post"
    />
  );
}
